import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Edit2, Trash2, Save, Settings, Clock, ChefHat } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL || '';

const KitchenSettings = () => {
  const [courseTypes, setCourseTypes] = useState([]);
  const [settings, setSettings] = useState({
    sound_alerts_enabled: true,
    alert_volume: 80,
    auto_refresh_enabled: true,
    refresh_interval_seconds: 10,
    overdue_threshold_minutes: 15,
    warning_threshold_minutes: 10
  });
  const [loading, setLoading] = useState(true);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  
  const [courseForm, setCourseForm] = useState({
    name: '',
    display_order: 0,
    sequence_order: 0,
    default_prep_time: 7,
    is_active: true
  });

  useEffect(() => {
    fetchCourseTypes();
    fetchSettings();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCourseTypes = async () => {
    try {
      const response = await axios.get(`${API}/api/kitchen/course-types`, {
        headers: getAuthHeader()
      });
      setCourseTypes(response.data);
    } catch (error) {
      console.error('Error fetching course types:', error);
      toast.error('Failed to load course types');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/kitchen/settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    }
  };

  const handleInitializeKitchen = async () => {
    if (!window.confirm('This will initialize the kitchen system with default course types and update the Kitchen role. Continue?')) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/api/kitchen/initialize`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Kitchen system initialized successfully!');
      fetchCourseTypes(); // Refresh course types
      console.log('Initialization result:', response.data);
    } catch (error) {
      console.error('Error initializing kitchen:', error);
      toast.error('Failed to initialize kitchen system');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(
        `${API}/api/kitchen/settings`,
        settings,
        { headers: getAuthHeader() }
      );
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleCreateCourseType = async () => {
    try {
      await axios.post(
        `${API}/api/kitchen/course-types`,
        courseForm,
        { headers: getAuthHeader() }
      );
      toast.success('Course type created successfully');
      setShowCourseModal(false);
      resetCourseForm();
      fetchCourseTypes();
    } catch (error) {
      console.error('Error creating course type:', error);
      toast.error('Failed to create course type');
    }
  };

  const handleUpdateCourseType = async () => {
    try {
      await axios.put(
        `${API}/api/kitchen/course-types/${editingCourse.id}`,
        courseForm,
        { headers: getAuthHeader() }
      );
      toast.success('Course type updated successfully');
      setShowCourseModal(false);
      setEditingCourse(null);
      resetCourseForm();
      fetchCourseTypes();
    } catch (error) {
      console.error('Error updating course type:', error);
      toast.error('Failed to update course type');
    }
  };

  const handleDeleteCourseType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this course type?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/kitchen/course-types/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Course type deleted successfully');
      fetchCourseTypes();
    } catch (error) {
      console.error('Error deleting course type:', error);
      toast.error('Failed to delete course type');
    }
  };

  const openEditModal = (course) => {
    setEditingCourse(course);
    setCourseForm({
      name: course.name,
      display_order: course.display_order,
      sequence_order: course.sequence_order || course.display_order,
      default_prep_time: course.default_prep_time,
      is_active: course.is_active
    });
    setShowCourseModal(true);
  };

  const resetCourseForm = () => {
    setCourseForm({
      name: '',
      display_order: 0,
      sequence_order: 0,
      default_prep_time: 7,
      is_active: true
    });
    setEditingCourse(null);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-8 h-8 text-indigo-600" />
            Kitchen Settings
          </h1>
          <p className="text-gray-600 mt-1">
            Configure course types, prep times, and kitchen panel settings
          </p>
        </div>

        {/* Course Types Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Course Types</h2>
            <div className="flex gap-2">
              {courseTypes.length === 0 && (
                <button
                  onClick={handleInitializeKitchen}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Initialize Kitchen System
                </button>
              )}
              <button
                onClick={() => {
                  resetCourseForm();
                  setShowCourseModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Course Type
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courseTypes.map(course => (
              <div
                key={course.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{course.name}</h3>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{course.default_prep_time} minutes</span>
                    </div>
                    <div className="mt-1">
                      <span className={`text-xs px-2 py-1 rounded ${
                        course.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {course.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(course)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {!course.restaurant_id && (
                      <span className="text-gray-400" title="Global course type cannot be deleted">
                        <Trash2 className="w-4 h-4" />
                      </span>
                    )}
                    {course.restaurant_id && (
                      <button
                        onClick={() => handleDeleteCourseType(course.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {courseTypes.length === 0 && (
            <div className="text-center py-12">
              <ChefHat className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No course types configured</p>
              <p className="text-gray-400 text-sm mt-2">
                Add course types to organize kitchen workflow
              </p>
            </div>
          )}
        </div>

        {/* Kitchen Panel Settings */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Panel Settings</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Auto Refresh */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <input
                  type="checkbox"
                  checked={settings.auto_refresh_enabled}
                  onChange={(e) => setSettings({...settings, auto_refresh_enabled: e.target.checked})}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                Enable Auto Refresh
              </label>
              {settings.auto_refresh_enabled && (
                <div className="ml-6">
                  <label className="block text-sm text-gray-600 mb-1">
                    Refresh Interval (seconds)
                  </label>
                  <input
                    type="number"
                    value={settings.refresh_interval_seconds}
                    onChange={(e) => setSettings({...settings, refresh_interval_seconds: parseInt(e.target.value)})}
                    min="5"
                    max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Sound Alerts */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <input
                  type="checkbox"
                  checked={settings.sound_alerts_enabled}
                  onChange={(e) => setSettings({...settings, sound_alerts_enabled: e.target.checked})}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                Enable Sound Alerts
              </label>
              {settings.sound_alerts_enabled && (
                <div className="ml-6">
                  <label className="block text-sm text-gray-600 mb-1">
                    Alert Volume (%)
                  </label>
                  <input
                    type="range"
                    value={settings.alert_volume}
                    onChange={(e) => setSettings({...settings, alert_volume: parseInt(e.target.value)})}
                    min="0"
                    max="100"
                    className="w-full"
                  />
                  <span className="text-sm text-gray-500">{settings.alert_volume}%</span>
                </div>
              )}
            </div>

            {/* Timing Thresholds */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overdue Threshold (minutes)
              </label>
              <input
                type="number"
                value={settings.overdue_threshold_minutes}
                onChange={(e) => setSettings({...settings, overdue_threshold_minutes: parseInt(e.target.value)})}
                min="5"
                max="60"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Orders exceeding this time will be marked as overdue
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Warning Threshold (minutes)
              </label>
              <input
                type="number"
                value={settings.warning_threshold_minutes}
                onChange={(e) => setSettings({...settings, warning_threshold_minutes: parseInt(e.target.value)})}
                min="1"
                max="30"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Show warning when orders approach this time
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveSettings}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              Save Settings
            </button>
          </div>
        </div>
      </div>

      {/* Course Type Modal */}
      {showCourseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {editingCourse ? 'Edit Course Type' : 'Add Course Type'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={courseForm.name}
                  onChange={(e) => setCourseForm({...courseForm, name: e.target.value})}
                  placeholder="e.g., Appetizer, Main Course"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Prep Time (minutes) *
                </label>
                <input
                  type="number"
                  value={courseForm.default_prep_time}
                  onChange={(e) => setCourseForm({...courseForm, default_prep_time: parseInt(e.target.value)})}
                  min="1"
                  max="120"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={courseForm.display_order}
                  onChange={(e) => setCourseForm({...courseForm, display_order: parseInt(e.target.value)})}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Display position in menus</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cooking Sequence Order
                </label>
                <input
                  type="number"
                  value={courseForm.sequence_order}
                  onChange={(e) => setCourseForm({...courseForm, sequence_order: parseInt(e.target.value)})}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Order of cooking (1=first, 2=second, etc.)</p>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={courseForm.is_active}
                    onChange={(e) => setCourseForm({...courseForm, is_active: e.target.checked})}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCourseModal(false);
                  setEditingCourse(null);
                  resetCourseForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingCourse ? handleUpdateCourseType : handleCreateCourseType}
                disabled={!courseForm.name || !courseForm.default_prep_time}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingCourse ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default KitchenSettings;
